import { StorageOfferResource } from 'app/storage-offer/storage-offer.resource';
import { UtilService } from 'app/_services/util.service';
import { StorageLocationResource } from 'app/_resources/storage-location.resource';
import { SsoSettingsResource } from 'app/_resources/sso-settings.resource';
import { bind } from 'app/_decorators/bind.decorator';
import * as angular from 'angular';
import { StorageEnums } from 'app/storage-offer/storage-enum-values';
import { Growl } from 'angular/custom/services/growl.service';
import { Attachment } from '../custom';
import { ErrorHandler } from 'app/_services/error-handler.factory';

enum PageState {
    LOADING,
    CREATION,
    CONFIRMATION,
    SENDING,
    SUCCESS,
    LOADING_ERROR
}

class CreateStorageOfferComponentCtrl implements ng.IController {
    public authenticatedUser: LoginResult;
    public allocationServiceEnabled: boolean;
    public servicePackage: StoragePremiumServicePackage;
    public pageEnum = PageState;
    public enums = StorageEnums;
    public selectableAllocationMethods: Array<AllocationMethod> = [
        'NO_ALLOCATION',
        'NO_ALLOCATION_2_BIDDING_ROUNDS'
    ];
    public multiRoundAllocationMethod: String =
        'NO_ALLOCATION_2_BIDDING_ROUNDS';
    public state: PageState;
    public offer: StorageOfferDto;
    public files: Attachment[] = [];
    public uploadingFile: boolean = false;
    public now: Date;
    public locationList: StorageLocationDto[];
    public selectedLocationName: string;
    public selectedLocationValidity: Interval;
    loadingStorageLocations: boolean = true;
    public creditLimitCheckEnabled: boolean;

    constructor(
        private $window: ng.IWindowService,
        private storageOfferResource: StorageOfferResource,
        private utilService: UtilService,
        private $q: ng.IQService,
        private growl: Growl,
        private storageLocationResource: StorageLocationResource,
        private ssoSettingsResource: SsoSettingsResource,
        private errorHandler: ErrorHandler
    ) {
        this.now = new Date();
        this.state = PageState.LOADING;
        this.offer = this.generateOffer();
        this.addProduct();
    }

    $onInit() {
        let allocationRequest =
            this.ssoSettingsResource.queryAllocationServiceSubscription(
                { id: this.authenticatedUser.referenceId },
                (
                    subscription: StoragePremiumServiceSubscriptionCreationDto
                ) => {
                    this.servicePackage = subscription.servicePackage;
                    this.setSelectableAllocationMethods(subscription);
                },
                this.errorHandler
            ).$promise;

        let locationRequest = this.storageLocationResource.getAll(
            (res: StorageLocationDto[]) => (this.locationList = res),
            this.errorHandler
        ).$promise;

        let creditLimitRequest = this.ssoSettingsResource.creditLimitEnabled(
            {},
            (res: boolean) => {
                this.creditLimitCheckEnabled = res;
            },
            this.errorHandler
        ).$promise;

        let allPromises: ng.IPromise<any>[] = [
            allocationRequest,
            locationRequest,
            creditLimitRequest
        ];

        this.$q
            .all(allPromises)
            .then(() => (this.state = PageState.CREATION))
            .catch(() => (this.state = PageState.LOADING_ERROR));
    }

    private setSelectableAllocationMethods(
        subscription: StoragePremiumServiceSubscriptionCreationDto
    ) {
        switch (this.servicePackage) {
            case 'NO_ALGORITHM':
                break;
            case 'SINGLE_ALGORITHM':
                this.selectableAllocationMethods.push(
                    subscription.allocationMethod
                );
                break;
            case 'MULTI_ALGORITHM':
                this.selectableAllocationMethods =
                    this.enums.allocationMethodValues;
                break;
        }
    }

    private generateOffer(): StorageOfferDto {
        return {
            id: null,
            offerLabel: '',
            storageLocationName: '(^_^)', // set to something until the String field storageLocation is removed.
            storageLocationRefId: null,
            storageLocationDto: null,
            runtime: this.utilService.getRunTimeSuggestion('DAY'),
            publicationDateAndTime: new Date(
                this.utilService.getNextGasDayStart()
            ),
            offerStart: new Date(this.utilService.getNextGasDayStart()),
            offerClose: new Date(this.utilService.getNextGasDayStart()),
            productType: 'SBU',
            unit: 'SBU',
            allocationMethod: 'NO_ALLOCATION',
            note: '',
            state: 'DRAFT',
            ssoId: null,
            ssoName: null,
            ssoShortName: null,
            products: <StorageProductDto[]>[],
            attachments: [],
            ssoLogo: null,
            actions: null,
            termsUrl: null,
            productPeriod: null,
            storageContracts: [],
            allocationState: null,
            minimumQuantity: null
        };
    }

    private generateProduct(): StorageProductDto {
        let productComponentsOfFirstProduct: StorageProductComponentDto[] = [
            this.generateProductComponent()
        ];
        let availableQuantityOfFirstProduct: StorageQuantityDto = {
            value: 1,
            unit: this.offer.unit
        };

        let biddingPriceUnitOfFirstProduct: StorageBiddingPriceUnitDto = {
            storageCurrency: 'CENT',
            storageQuantityUnit: this.offer.unit,
            storagePriceRuntime: 'RUNTIME'
        };

        return {
            id: null,
            productLabel: '',
            availableQuantity: availableQuantityOfFirstProduct,
            biddingPriceUnit: biddingPriceUnitOfFirstProduct,
            reservePrice: null,
            productComponents: productComponentsOfFirstProduct
        };
    }

    private generateProductComponent(): StorageProductComponentDto {
        return {
            id: null,
            capacityCategory: 'FIRM',
            runtime: this.utilService.getRunTimeSuggestion('DAY'),
            storageVolume: null,
            injectionCapacity: null,
            withdrawalCapacity: null
        };
    }

    public switchToCreationPage(): void {
        this.state = PageState.CREATION;
    }

    public confirm(): void {
        this.state = PageState.SENDING;
        if (this.offer.allocationMethod == 'UNIFORM_PRICE_MATCHING') {
            this.setAvailableQuantityToZero();
        }
        if (this.offer.allocationMethod == 'NO_ALLOCATION_2_BIDDING_ROUNDS') {
            //ToDo: Do we need to do something here? clean up model maybe...?
        }
        for (let product of this.offer.products) {
            if (!!product.reservePrice) {
                product.reservePrice.unit = product.biddingPriceUnit;
            }
        }
        this.storageOfferResource.save(
            this.offer,
            () => {
                this.growl.show(
                    'The offer has been uploaded successfully.',
                    'success'
                );
                this.state = PageState.SUCCESS;
            },
            (err: ng.IHttpPromiseCallbackArg<RestError>) => {
                const errorMessage =
                    err && err.data && err.data.message
                        ? err.data.message
                        : 'An unknown error occurred. Please, check back later.';
                const errorCode =
                    err && err.data && err.data.errorCode
                        ? err.data.errorCode
                        : '';
                this.growl.show(errorMessage, 'danger', { errorCode });
            }
        );
    }

    private setAvailableQuantityToZero() {
        this.offer.products.forEach((p) => (p.availableQuantity.value = 0));
    }

    public addProduct(): void {
        this.offer.products.push(this.generateProduct());
    }

    public removeProduct(index: number): void {
        this.offer.products.splice(index, 1);
    }

    public addProductComponent(index: number): void {
        this.offer.products[index].productComponents.push(
            this.generateProductComponent()
        );
    }

    public removeProductComponent(
        productIndex: number,
        productComponentIndex: number
    ): void {
        this.offer.products[productIndex].productComponents.splice(
            productComponentIndex,
            1
        );
    }

    public changeUnitOfProducts(): void {
        for (let product of this.offer.products) {
            product.availableQuantity.unit = this.offer.unit;
            product.biddingPriceUnit.storageQuantityUnit = this.offer.unit;
        }
    }

    public addAttachment(): void {
        let uploadPromises = [];
        let index = 0;

        for (let file of this.files) {
            let name = file.name ? file.name : 'file' + (index + 1) + '.pdf';
            index++;

            let formData = new FormData();
            formData.append('file', file.file);
            formData.append('name', name);

            let promise = this.storageOfferResource.upload(
                {},
                formData,
                (res: StorageDocumentDto) => {
                    this.uploadingFile = true;
                    this.offer.attachments.push(res);
                },
                this.errorHandler
            ).$promise;
            uploadPromises.push(promise);
        }

        this.$q.all(uploadPromises).finally(() => {
            this.files = [];
            this.uploadingFile = false;
        });
    }

    public removeAttachment(attachmentIndex: number): void {
        this.offer.attachments.splice(attachmentIndex, 1);
    }

    public submit(form: ng.IFormController): void {
        if (!form.$valid) {
            this.$window.scrollTo(0, 0);
            return;
        }
        this.state = PageState.CONFIRMATION;
    }

    public setLocation(location: StorageLocationDto): void {
        this.selectedLocationName = location.name;
        this.selectedLocationValidity = location.validity;
        this.offer.storageLocationName = location.name;
        this.offer.storageLocationRefId = location.refId;
        this.offer.storageLocationDto = location;
    }

    public evaluateMatchingStorageLocation() {
        delete this.offer.storageLocationRefId;
        delete this.selectedLocationValidity;

        if (!!this.selectedLocationName && !!this.locationList) {
            angular.forEach(
                this.locationList,
                (location: StorageLocationDto) => {
                    if (location.name == this.selectedLocationName) {
                        this.setLocation(location);
                    }
                }
            );
        }
    }
}

angular.module('app').component('pkpCreateStorageOffer', {
    controller: CreateStorageOfferComponentCtrl,
    template: require('app/storage-offer/create-storage-offer.component.html'),
    bindings: {
        authenticatedUser: '<'
    }
});
